"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TriangleAlert, ShieldAlert, ThermometerSun, Droplets, Bug } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  id: "item-1",
  question: "How does Port Charlotte's high humidity affect pest activity?",
  answer: "The constant moisture and high humidity in Port Charlotte create an ideal breeding ground for mosquitoes, roaches, and subterranean termites. Our treatments are specifically formulated to withstand these humid conditions and maintain a strong protective barrier around your property.",
  icon: Droplets
}, {
  id: "item-2",
  question: "Are termites a year-round threat in this area?",
  answer: "Yes. Florida's warm, subtropical climate means that both subterranean and drywood termites remain active 365 days a year. Without a winter freeze to slow them down, regular inspections and proactive treatments are crucial for protecting your home's structural integrity.",
  icon: TriangleAlert
}, {
  id: "item-3",
  question: "What is the best way to control mosquitoes near the Peace River?",
  answer: "Properties located near the Peace River or local canals require aggressive, targeted mosquito control. We focus on identifying and treating standing water breeding sites, combined with specialized barrier treatments applied to foliage where adult mosquitoes rest during the day.",
  icon: Bug
}, {
  id: "item-4",
  question: "Why am I seeing more spiders inside my home during the rainy season?",
  answer: "Heavy seasonal rains drive insects indoors seeking shelter, and predatory spiders simply follow their food source. By implementing a robust perimeter treatment, we eliminate the primary food source and create a barrier that keeps both insects and spiders outside.",
  icon: ThermometerSun
}, {
  id: "item-5",
  question: "Do you offer free inspections in Port Charlotte?",
  answer: "Absolutely. We provide comprehensive, no-obligation pest and termite inspections for all residential and commercial properties in Port Charlotte, Punta Gorda, and surrounding areas. Our licensed technicians will assess your unique situation and recommend a targeted solution.",
  icon: ShieldAlert
}];
export default function PortCharlotteFaq() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="portcharlottefaq" className="bg-background py-24 px-4 md:px-8 overflow-hidden relative">
      {}
      <div className="absolute top-0 left-0 w-full h-4 bg-foreground opacity-10" />
      <div className="absolute bottom-0 left-0 w-full h-4 bg-foreground opacity-10" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-full bg-foreground opacity-5" />

      <div ref={ref} className={`max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        {}
        <div className="col-span-1 md:col-span-12 text-center flex flex-col items-center justify-center mb-8">
          <Badge variant="outline" className="border-4 border-foreground text-foreground rounded-none px-6 py-2 text-sm md:text-base font-default uppercase tracking-widest mb-6 bg-card">
            <TriangleAlert className="w-5 h-5 mr-2 inline-block" />
            Local Climate & Pest Guide
          </Badge>
          <h2 className="text-5xl md:text-7xl lg:text-8xl font-default text-foreground uppercase leading-[0.9] tracking-tighter max-w-5xl">
            Port Charlotte <br className="hidden md:block" />
            <span className="text-card bg-foreground px-4 inline-block mt-2 transform -skew-x-6">
              Pest Conditions
            </span>
          </h2>
          <p className="mt-8 text-lg md:text-xl font-serif text-foreground max-w-2xl mx-auto font-medium border-l-4 border-foreground pl-6 text-left">
            Understanding how our unique coastal climate impacts pest behavior is the first step in effective prevention. Here is what you need to know about protecting your property in Southwest Florida.
          </p>
        </div>

        {}
        <div className="col-span-1 md:col-span-8 md:col-start-3">
          <Accordion type="single" collapsible className="w-full space-y-6">
            {faqs.map((faq, index) => {
            const Icon = faq.icon;
            return <AccordionItem key={faq.id} value={faq.id} data-index={index} className="border-4 border-foreground bg-card rounded-none overflow-hidden group">
                  <AccordionTrigger className="px-6 py-5 hover:no-underline hover:bg-primary/20 transition-colors data-[state=open]:bg-foreground data-[state=open]:text-card">
                    <div className="flex items-center gap-4 text-left">
                      <div className="bg-primary p-2 border-2 border-foreground group-data-[state=open]:border-card group-data-[state=open]:bg-card group-data-[state=open]:text-foreground shrink-0">
                        <Icon className="w-6 h-6" />
                      </div>
                      <span className="font-default text-xl md:text-2xl uppercase tracking-tight">
                        {faq.question}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pt-6 pb-8 font-serif text-lg md:text-xl text-foreground bg-card border-t-4 border-foreground">
                    <div className="pl-16">
                      {faq.answer}
                    </div>
                  </AccordionContent>
                </AccordionItem>;
          })}
          </Accordion>

          {}
          <div className="mt-12 text-center bg-foreground p-8 border-4 border-foreground relative overflow-hidden">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGcgc3Ryb2tlPSIjZmZmIiBzdHJva2Utd2lkdGg9IjEiIGZpbGw9Im5vbmUiIG9wYWNpdHk9IjAuMDUiPjxwYXRoIGQ9Ik0wIDQwaDQwVjBIMHoiLz48L2c+PC9zdmc+')] opacity-20"></div>
            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-left">
                <h3 className="text-2xl md:text-3xl font-default text-card uppercase mb-2">
                  Need Immediate Assistance?
                </h3>
                <p className="text-card/80 font-serif text-lg">
                  Our local experts are ready to inspect your property.
                </p>
              </div>
              <Button asChild size="lg" className="rounded-none border-4 border-primary bg-primary text-foreground hover:bg-card hover:text-foreground hover:border-card text-lg font-default uppercase tracking-wider h-14 px-8 w-full md:w-auto">
                <Link to="/ContactPage">
                  Schedule Free Inspection
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}